/* ==========================================================================
   pdp.js — Reli. PDP v2
   ONE state object drives the desktop buy box and the mobile pinned bar.
   Every control is queried with querySelectorAll, so both surfaces update
   from the same render pass and cannot disagree.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pdp]');
  if (!root) return;

  var d = root.dataset;

  var cfg = {
    caseVariant: d.caseVariant || '',
    palletVariant: d.palletVariant || '',
    casePrice: parseInt(d.casePrice, 10) || 0,        // cents, one case
    palletPrice: parseInt(d.palletPrice, 10) || 0,    // cents, one pallet
    casesPerPallet: parseInt(d.casesPerPallet, 10) || 0,
    hasPallet: d.hasPallet === 'true',
    showCaseMode: d.showCaseMode !== 'false',
    caseStockAvailable: d.caseStockAvailable !== 'false',
    palletStockLimited: d.palletStockLimited === 'true',
    maxPallets: parseInt(d.maxPallets, 10) || 0,
    isSignedIn: d.signedIn === 'true',
    moneyFormat: d.moneyFormat || '${{amount}}',
    cartAction: d.cartAction || '/cart/add.js',
    priceUnitCase: d.priceUnitCase || '/ case',
    priceUnitPallet: d.priceUnitPallet || '/ pallet',
    qtyLabelCase: d.qtyLabelCase || 'Cases',
    qtyLabelPallet: d.qtyLabelPallet || 'Number of pallets',
    subtotalLabel: d.subtotalLabel || 'Subtotal'
  };

  /* If the sign-up form just reloaded with a validation error, it's already
     rendered open — start in pallet mode so the customer actually sees it,
     instead of it being hidden behind the default case-mode view. Same
     default when there's no case tab to begin with (showCaseMode false) —
     there's nothing case-mode to land on. */
  var signupHasErrors = !!root.querySelector('[data-pdp-signup-form][data-has-errors="true"]');
  var startInPallet = signupHasErrors || !cfg.showCaseMode;
  var state = { mode: startInPallet ? 'pallet' : 'case', qty: { case: 1, pallet: 1 } };

  /* ---- money ------------------------------------------------------------
     Formats cents using the shop's money_format so multi-currency and
     non-USD shops stay correct. Handles amount, amount_no_decimals and the
     comma_separator variants Shopify emits. */
  function formatMoney(cents) {
    var value = (cents / 100);
    var fmt = cfg.moneyFormat;
    function group(num, decimals, thousands, decimal) {
      var fixed = Math.abs(num).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      if (name === 'amount') return group(value, 2, ',', '.');
      if (name === 'amount_no_decimals') return group(value, 0, ',', '.');
      if (name === 'amount_with_comma_separator') return group(value, 2, '.', ',');
      if (name === 'amount_no_decimals_with_comma_separator') return group(value, 0, '.', ',');
      if (name === 'amount_with_space_separator') return group(value, 2, ' ', ',');
      return group(value, 2, ',', '.');
    });
  }

  /* ---- tiers ------------------------------------------------------------
     Read straight off the rendered ladder rows, so Liquid stays the single
     source of truth for the pricing tiers. */
  function readTiers(mode) {
    var rows = root.querySelectorAll('[data-pdp-ladder="' + mode + '"] [data-pdp-tier]');
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      out.push({
        n: parseInt(rows[i].getAttribute('data-qty'), 10),
        pct: parseFloat(rows[i].getAttribute('data-pct')) || 0,
        el: rows[i]
      });
    }
    return out;
  }

  var tiers = { case: readTiers('case'), pallet: readTiers('pallet') };

  function isCase() { return state.mode === 'case'; }
  function qty() { return state.qty[state.mode]; }
  function unitPrice() { return isCase() ? cfg.casePrice : cfg.palletPrice; }

  function activeTier() {
    var list = tiers[state.mode];
    if (!list.length) return { n: 1, pct: 0 };
    var match = list[0];
    for (var i = 0; i < list.length; i++) {
      if (qty() >= list[i].n) match = list[i];
    }
    return match;
  }

  function selectionText() {
    var n = qty();
    if (isCase()) return n + (n === 1 ? ' case' : ' cases');
    var cases = n * cfg.casesPerPallet;
    return n + (n === 1 ? ' pallet' : ' pallets') + (cases ? ' (' + cases + ' cases)' : '');
  }

  function setAll(selector, fn) {
    var nodes = root.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i], i);
  }

  function setText(selector, text) {
    setAll(selector, function (el) { el.textContent = text; });
  }

  /* ---- render ---------------------------------------------------------- */
  function render() {
    var caseMode = isCase();
    var n = qty();
    var tier = activeTier();
    var pct = tier.pct;
    var keep = (100 - pct) / 100;

    var listTotal = unitPrice() * n;
    var orderTotal = Math.round(listTotal * keep);
    var savings = listTotal - orderTotal;
    var perCaseNow = Math.round(cfg.casePrice * keep);
    var unitNow = Math.round(unitPrice() * keep);

    /* toggle + panels */
    setAll('[data-pdp-mode]', function (el) {
      el.setAttribute('aria-pressed', String(el.getAttribute('data-pdp-mode') === state.mode));
    });
    setAll('[data-pdp-ladder]', function (el) {
      el.hidden = el.getAttribute('data-pdp-ladder') !== state.mode;
    });
    var noPallet = !caseMode && !cfg.hasPallet;
    var caseOutOfStock = caseMode && !cfg.caseStockAvailable;
    setAll('[data-pdp-buy-region]', function (el) { el.hidden = noPallet || caseOutOfStock; });
    setAll('[data-pdp-no-pallet]', function (el) { el.hidden = !noPallet; });
    setAll('[data-pdp-case-oos]', function (el) { el.hidden = !caseOutOfStock; });
    setAll('[data-pdp-pallet-stock-note]', function (el) {
      el.hidden = caseMode || !cfg.palletStockLimited;
    });
    if (noPallet || caseOutOfStock) return;

    /* Pallet pricing is visible to everyone; buying a pallet requires being
       signed in. Guests get the sign-up prompt instead of the stepper/buy
       buttons — case purchasing is untouched either way. */
    var guestPalletGate = !caseMode && !cfg.isSignedIn;
    setAll('[data-pdp-purchase-controls]', function (el) { el.hidden = guestPalletGate; });
    setAll('[data-pdp-guest-pallet-note]', function (el) { el.hidden = !guestPalletGate; });

    /* price block */
    setText('[data-pdp-price-main]', formatMoney(unitNow));
    setText('[data-pdp-price-unit]', caseMode ? cfg.priceUnitCase : cfg.priceUnitPallet);
    setAll('[data-pdp-price-was]', function (el) {
      el.hidden = pct <= 0;
      el.textContent = formatMoney(unitPrice());
    });
    var sub;
    if (caseMode) {
      sub = pct
        ? pct + '% off at ' + n + ' cases · list ' + formatMoney(cfg.casePrice) + '/cs'
        : 'Discounts start at ' + (tiers.case[1] ? tiers.case[1].n + ' cases (' + tiers.case[1].pct + '% off)' : 'higher quantities');
    } else {
      sub = formatMoney(perCaseNow) + ' per case · ' + cfg.casesPerPallet + ' cases per pallet';
    }
    setText('[data-pdp-price-sub]', sub);

    /* active ladder row — recomputed every render, never only on load */
    var list = tiers[state.mode];
    for (var i = 0; i < list.length; i++) {
      list[i].el.setAttribute('aria-current', String(list[i] === tier && n >= list[0].n));
    }

    /* quantity */
    setAll('[data-pdp-qty-input]', function (el) { if (el.value !== String(n)) el.value = n; });
    setText('[data-pdp-qty-label]', caseMode ? cfg.qtyLabelCase : cfg.qtyLabelPallet);

    /* subtotal */
    setText('[data-pdp-subtotal-label]', cfg.subtotalLabel + ' · ' + selectionText());
    setText('[data-pdp-subtotal]', formatMoney(orderTotal));
    setText('[data-pdp-savings]', savings > 0
      ? 'You save ' + formatMoney(savings) + ' on this order (' + pct + '% off)'
      : '');

    /* mobile bar summary */
    setText('[data-pdp-bar-note]', pct
      ? selectionText() + ' · ' + pct + '% off · save ' + formatMoney(savings)
      : selectionText() + ' · ' + formatMoney(perCaseNow) + '/cs');
  }

  /* ---- state setters --------------------------------------------------- */
  function setMode(next) {
    if (next !== 'case' && next !== 'pallet') return;
    state.mode = next;
    render();
  }
  function setQty(value) {
    var n = parseInt(value, 10);
    if (isNaN(n) || n < 1) n = 1;
    if (!isCase() && cfg.palletStockLimited && n > cfg.maxPallets) n = cfg.maxPallets;
    state.qty[state.mode] = n;
    render();
  }
  function switchToCase() {
    /* Carries the customer's intended volume over as an equivalent case
       quantity, rather than resetting to 1 — they already sized their order,
       switching pricing basis shouldn't throw that away. */
    var equivalent = qty() * cfg.casesPerPallet;
    state.mode = 'case';
    setQty(equivalent || 1);
  }
  function switchToPallet() {
    /* Mirror of switchToCase — cases can't split evenly into pallets, so this
       rounds UP rather than down (never quote fewer pallets than the volume
       they asked for). Mode is set BEFORE calling setQty() on purpose: that's
       what makes setQty()'s existing palletStockLimited clamp apply here too,
       so this can never land on more pallets than are actually in stock. */
    var equivalent = cfg.casesPerPallet ? Math.ceil(qty() / cfg.casesPerPallet) : 1;
    state.mode = 'pallet';
    setQty(equivalent || 1);
  }

  /* ---- cart ------------------------------------------------------------ */
  function payload() {
    var n = qty();
    if (isCase()) {
      return { items: [{ id: Number(cfg.caseVariant), quantity: n }] };
    }
    var caseQty = cfg.casesPerPallet ? n * cfg.casesPerPallet : n;
    var props = { 'Pallet quantity': String(n) };
    if (cfg.casesPerPallet) props['Cases'] = String(caseQty);
    return { items: [{ id: Number(cfg.palletVariant), quantity: caseQty, properties: props }] };
  }

  function flash(message, isError) {
    setAll('[data-pdp-flash]', function (el) {
      el.textContent = message;
      el.classList.toggle('pdp-flash--error', !!isError);
    });
    clearTimeout(flash.timer);
    flash.timer = setTimeout(function () {
      setAll('[data-pdp-flash]', function (el) {
        el.textContent = '';
        el.classList.remove('pdp-flash--error');
      });
    }, 4000);
  }

  function busy(on) {
    setAll('[data-pdp-add], [data-pdp-buy]', function (el) { el.disabled = on; });
  }

  function addToCart(then) {
    if (!isCase() && !cfg.palletVariant) {
      flash('No pallet SKU is linked to this product.', true);
      return;
    }
    busy(true);
    fetch(cfg.cartAction, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload())
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart.');
          return body;
        });
      })
      .then(function () {
        busy(false);
        if (then) then();
        else {
          flash('Added to cart — ' + selectionText());
          openCartDrawer();
        }
      })
      .catch(function (err) {
        busy(false);
        flash(err.message, true);
      });
  }

  /* Works with Dawn-family cart drawers; falls back to the cart page. */
  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      drawer.open();
      return;
    }
    var bubble = document.querySelector('#cart-icon-bubble, .header__icon--cart');
    if (bubble) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }
    window.location = '/cart';
  }

  /* ---- events ---------------------------------------------------------- */
  root.addEventListener('click', function (e) {
    var mode = e.target.closest('[data-pdp-mode]');
    if (mode) { setMode(mode.getAttribute('data-pdp-mode')); return; }

    var showSignup = e.target.closest('[data-pdp-show-signup]');
    if (showSignup) {
      setAll('[data-pdp-signup-prompt]', function (el) { el.hidden = true; });
      setAll('[data-pdp-signup-form]', function (el) { el.hidden = false; });
      var signupForm = root.querySelector('[data-pdp-signup-form]');
      if (signupForm) {
        signupForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var firstField = signupForm.querySelector('input');
        if (firstField) firstField.focus({ preventScroll: true });
      }
      return;
    }

    var hideSignup = e.target.closest('[data-pdp-hide-signup]');
    if (hideSignup) {
      setAll('[data-pdp-signup-form]', function (el) { el.hidden = true; });
      setAll('[data-pdp-signup-prompt]', function (el) { el.hidden = false; });
      return;
    }

    var switchCase = e.target.closest('[data-pdp-switch-to-case]');
    if (switchCase) { switchToCase(); return; }

    var switchPallet = e.target.closest('[data-pdp-switch-to-pallet]');
    if (switchPallet) { switchToPallet(); return; }

    var tierRow = e.target.closest('[data-pdp-tier]');
    if (tierRow) { setQty(tierRow.getAttribute('data-qty')); return; }

    var step = e.target.closest('[data-pdp-step]');
    if (step) { setQty(qty() + parseInt(step.getAttribute('data-pdp-step'), 10)); return; }

    var add = e.target.closest('[data-pdp-add]');
    if (add) { e.preventDefault(); addToCart(null); return; }

    var buy = e.target.closest('[data-pdp-buy]');
    if (buy) {
      e.preventDefault();
      addToCart(function () { window.location = '/checkout'; });
      return;
    }

    var thumb = e.target.closest('[data-pdp-thumb]');
    if (thumb) {
      if (thumb.hasAttribute('data-pdp-thumb-overflow')) {
        setAll('[data-pdp-thumb-extra]', function (el) { el.hidden = false; });
        thumb.removeAttribute('data-pdp-thumb-overflow');
        thumb.classList.remove('pdp-thumb--overflow');
        var badge = thumb.querySelector('[data-pdp-thumb-overflow-badge]');
        if (badge) badge.remove();
      }
      var main = root.querySelector('[data-pdp-main-image]');
      if (main) {
        main.src = thumb.getAttribute('data-src');
        main.alt = thumb.getAttribute('data-alt') || '';
      }
      setAll('[data-pdp-thumb]', function (el) {
        el.setAttribute('aria-current', String(el === thumb));
      });
      return;
    }

    var tab = e.target.closest('[data-pdp-tab]');
    if (tab) {
      var id = tab.getAttribute('data-pdp-tab');
      setAll('[data-pdp-tab]', function (el) {
        el.setAttribute('aria-selected', String(el === tab));
      });
      setAll('[data-pdp-panel]', function (el) {
        el.hidden = el.getAttribute('data-pdp-panel') !== id;
      });
      return;
    }

    var faq = e.target.closest('[data-pdp-faq-q]');
    if (faq) {
      var open = faq.getAttribute('aria-expanded') !== 'true';
      faq.setAttribute('aria-expanded', String(open));
      var answer = document.getElementById(faq.getAttribute('aria-controls'));
      if (answer) answer.hidden = !open;
      var caret = faq.querySelector('[data-pdp-caret]');
      if (caret) caret.textContent = open ? '▲' : '▼';
    }
  });

  root.addEventListener('change', function (e) {
    var input = e.target.closest('[data-pdp-qty-input]');
    if (input) setQty(input.value);
  });

  /* Keyboard: ladder rows are real buttons, so Enter/Space work already. */

  /* ---- mobile bar reveal ---------------------------------------------- */
  var bar = document.querySelector('[data-pdp-bar]');
  var hero = root.querySelector('[data-pdp-hero]');
  if (bar && hero && 'IntersectionObserver' in window) {
    var sentinel = root.querySelector('[data-pdp-buybox]');
    new IntersectionObserver(function (entries) {
      bar.setAttribute('data-visible', String(!entries[0].isIntersecting));
    }, { rootMargin: '-40% 0px 0px 0px' }).observe(sentinel || hero);
  } else if (bar) {
    bar.setAttribute('data-visible', 'true');
  }

  /* ---- BOLD price interference guard ------------------------------------
     BOLD Custom Pricing scans the page for what it thinks is "the" price
     display and periodically rewrites it back to the plain regular price —
     confirmed live by inspecting the DOM: it tags data-pdp-price-main with
     its own "money pre-money" class and a data-product-id attribute the
     moment it finds it, then keeps re-asserting the case price over
     whatever pallet-mode pricing we just rendered. It doesn't understand
     our mode toggle at all (see its own "Unrecognized form" console
     warning). CSS can't stop a script from rewriting text, so this fights
     back the same way BOLD does: watch the element, and if anything but
     our own render() changes it, put our value right back. The apply flag
     stops this from reacting to its own corrections. */
  var priceMain = root.querySelector('[data-pdp-price-main]');
  if (priceMain && 'MutationObserver' in window) {
    var applyingPriceFix = false;
    new MutationObserver(function () {
      if (applyingPriceFix) return;
      /* BOLD doesn't always change the class and the text in the same pass —
         sometimes it tags the loading-style class first and rewrites the
         text a moment later. Correcting both on every mutation, independent
         of each other, closes that gap instead of only fixing the class
         when the text also happens to be wrong. */
      applyingPriceFix = true;
      var expected = formatMoney(Math.round(unitPrice() * ((100 - activeTier().pct) / 100)));
      if (priceMain.className !== 'pdp-price__main') priceMain.className = 'pdp-price__main';
      if (priceMain.textContent !== expected) priceMain.textContent = expected;
      applyingPriceFix = false;
    }).observe(priceMain, { characterData: true, childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  render();
})();
